/**
 * MY — "Annual Return" 카테고리 variable mapper.
 *
 * 현재 지원 task code:
 *   - M32115 Annual Return — 단일 문서 DCR (Lodgement of Annual Return)
 *
 * DCR 변수 (snake_case, case-sensitive — documentVariableReplacer.js):
 *   [[company_registration_no]]    회사 등록번호
 *   [[company_name]]               회사명
 *   [[if is_sole_director]]        단독 이사 여부 (sole/directors 분기)
 *   [[constitution_clause_no]]     정관 조항 번호
 *   [[ar_calendar_year]]           AR 대상 연도
 *   [[Signers]]                    이사 서명 (PreSet 단계 입력 안 함)
 *   [[resolution_date]]            결의서 날짜
 */

export interface AnnualReturnInput {
  companyName: string;
  registrationNo: string;
  /** 구 등록번호 — [[old_registration_no]] 치환용 (없으면 backend prepare-documents 가 entity 에서 보충). */
  registrationNoOld?: string;
  /** 정관 조항 — 번호("15") 또는 전체 문자열 허용. 전체 문자열이면 mapper 가 번호만 추출. */
  constitutionClauseNo?: string;
  /** Governing document type — 패널 라디오 값 (constitution/articles/third_schedule).
   *  제공되면 그대로 사용; 없으면 constitutionClauseNo 전문에서 자동 감지. */
  governingDocumentType?: 'constitution' | 'articles' | 'third_schedule';
  /** Annual Return 대상 연도 — L_ANNUAL_RETURN.FILING_YEAR */
  arCalendarYear: string | number;
  /** 단독 이사 여부 — true=single_director_company, false=multiple_directors_company */
  isSoleDirector: boolean;
  /** 결의서 날짜 (DD Month YYYY 또는 ISO; mapper 는 trim 만) */
  resolutionDate?: string;
  /** 외부 signers (PreSet 미입력 — prepare-documents 가 그대로 처리). 비워두면 사용자가 사후 선택. */
  signers?: any[];
}

/** 정관 조항 전체 문자열에서 조항 식별자(번호)만 추출. (AOD 패널 parseClauseNo 와 동일) */
function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

/** 정관 조항 전체 문자열에서 governing document type 감지. 기본값 constitution. */
function detectGoverningType(clauseFull: string): 'constitution' | 'articles' | 'third_schedule' {
  const s = String(clauseFull || '');
  if (/articles\s+of\s+association/i.test(s)) return 'articles';
  if (/third\s+schedule/i.test(s)) return 'third_schedule';
  return 'constitution';
}

export interface AnnualReturnDocFlags {
  /** Lodgement of Annual Return DCR 문서 포함 여부 */
  hasDcr: boolean;
  /** 어떤 문서든 적용되는 메인 폼 활성화 플래그 */
  needsForm: boolean;
}

function trim(v: any): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

export function buildAnnualReturnVariables(input: AnnualReturnInput): Record<string, any> {
  const companyName = trim(input.companyName);
  const companyRegistrationNo = trim(input.registrationNo);
  const oldRegistrationNo = trim(input.registrationNoOld);
  const clauseFull = trim(input.constitutionClauseNo);
  const constitutionClauseNumber = parseClauseNo(clauseFull); // "Article 15 of ..." → "15"
  // 패널 라디오 값 우선; 없으면 조항 전문에서 자동 감지.
  const govType = input.governingDocumentType || detectGoverningType(clauseFull);
  const arCalendarYear = trim(input.arCalendarYear);
  const resolutionDate = trim(input.resolutionDate);

  // [[if single_director_company]] / [[if multiple_directors_company]] 분기 — boolean.
  //   documentVariableReplacer 가 truthy/falsy 로 [[if NAME]]…[[endif]] 분기 처리.
  const isSoleDirector = !!input.isSoleDirector;

  const out: Record<string, any> = {
    company_name: companyName,
    company_registration_no: companyRegistrationNo,
    // 구 등록번호 — 비우면 backend prepare-documents 가 entity(REG_NO_OLD)에서 보충.
    old_registration_no: oldRegistrationNo,
    ar_calendar_year: arCalendarYear,
    resolution_date: resolutionDate,

    // ── 조항 번호 (신규: [[constitution_clause_number]] / 레거시: [[constitution_clause_no]]) ──
    constitution_clause_number: constitutionClauseNumber,
    constitution_clause_no: constitutionClauseNumber,

    // ── governing document type 분기 — [[if constitution]] / [[if articles]] / [[if third_schedule]] ──
    constitution: govType === 'constitution',
    articles: govType === 'articles',
    third_schedule: govType === 'third_schedule',
    // DCR 템플릿 토큰 alias — [[if resolution_basis_constitution]] / _articles / _third_schedule (AOD mapper 동일).
    resolution_basis_constitution: govType === 'constitution',
    resolution_basis_articles: govType === 'articles',
    resolution_basis_third_schedule: govType === 'third_schedule',

    // ── 이사 수 분기 — [[if single_director_company]] / [[if multiple_directors_company]] ──
    single_director_company: isSoleDirector,
    multiple_directors_company: !isSoleDirector,
    // DCR 템플릿 토큰 alias — [[if is_sole_director_company]] / [[if is_multiple_director_company]] (AOD mapper 동일).
    is_sole_director_company: isSoleDirector,
    is_multiple_director_company: !isSoleDirector,
    // 레거시 호환 — [[if is_sole_director]] 사용 템플릿 대비.
    is_sole_director: isSoleDirector,

    // UPPER_CASE alias (레거시 템플릿 호환 — AOD mapper 와 동일 패턴)
    COMPANY_NAME: companyName,
    COMPANY_REGISTRATION_NO: companyRegistrationNo,
    CONSTITUTION_CLAUSE_NO: constitutionClauseNumber,
    AR_CALENDAR_YEAR: arCalendarYear,
    RESOLUTION_DATE: resolutionDate,
  };

  if (Array.isArray(input.signers) && input.signers.length > 0) {
    out.signers = input.signers;
  }

  return out;
}

export function detectAnnualReturnDocFlags(
  documents: Array<{ templateName?: string; docCode?: string }>
): AnnualReturnDocFlags {
  const names = (documents || []).map((d) => (d.templateName || '').toLowerCase());
  const hasDcr = names.some(
    (n) =>
      n.includes('lodgement of annual return') ||
      (n.includes('annual return') && (n.includes('dcr') || n.includes('resolution'))) ||
      n.includes('directors') && n.includes('circular') && n.includes('resolution')
  );
  return {
    hasDcr,
    needsForm: hasDcr,
  };
}
